import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tooltip,
    Card,
    CardContent,
    Grid,
} from '@mui/material';
import {
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Refresh as RefreshIcon,
    Flag as FlagIcon,
    RemoveCircle as RemoveCircleIcon,
    PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import api from '../services/api';
import UserActionMenu from '../components/UserActionMenu';

const SuspiciousBalances = () => {
    const [loading, setLoading] = useState(true);
    const [flaggedUsers, setFlaggedUsers] = useState([]);
    const [error, setError] = useState(null);
    const [detectionRunning, setDetectionRunning] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [unflagDialog, setUnflagDialog] = useState(false);
    const [unflagNotes, setUnflagNotes] = useState('');

    useEffect(() => {
        fetchFlaggedUsers();
    }, []);

    const fetchFlaggedUsers = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/admin/suspiciousBalances`, {
                method: 'GET',
                headers: api.getAuthHeaders(),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message);
            setFlaggedUsers(data.data || []);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to fetch flagged accounts');
        } finally {
            setLoading(false);
        }
    };

    const runManualDetection = async () => {
        try {
            setDetectionRunning(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/admin/detectSuspiciousBalances`, {
                method: 'POST',
                headers: api.getAuthHeaders(),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message);
            alert(data.message);
            await fetchFlaggedUsers(); // Refresh the list
        } catch (err) {
            alert(err.message || 'Detection failed');
        } finally {
            setDetectionRunning(false);
        }
    };

    const handleUnflag = async () => {
        if (!selectedUser) return;

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/admin/unflagSuspiciousBalance/${selectedUser._id}`, {
                method: 'PUT',
                headers: api.getAuthHeaders(),
                body: JSON.stringify({ notes: unflagNotes })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message);
            alert(`Successfully cleared flag for ${selectedUser.fullname}`);
            setUnflagDialog(false);
            setUnflagNotes('');
            setSelectedUser(null);
            await fetchFlaggedUsers();
        } catch (err) {
            alert(err.message || 'Failed to unflag account');
        }
    };

    const openUnflagDialog = (user) => {
        setSelectedUser(user);
        setUnflagDialog(true);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    const formatCurrency = (amount) => {
        return `₦${Number(amount).toLocaleString()}`;
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    🚨 Suspicious Balance Monitoring
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchFlaggedUsers}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={detectionRunning ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                        onClick={runManualDetection}
                        disabled={detectionRunning}
                    >
                        {detectionRunning ? 'Running Detection...' : 'Run Manual Detection'}
                    </Button>
                </Box>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography color="text.secondary" gutterBottom>
                                Flagged Accounts
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                {flaggedUsers.length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography color="text.secondary" gutterBottom>
                                Total Flagged Balance
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                                {formatCurrency(flaggedUsers.reduce((sum, user) => sum + (user.balance || 0), 0))}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography color="text.secondary" gutterBottom>
                                Detection Status
                            </Typography>
                            <Chip
                                icon={<CheckCircleIcon />}
                                label="Active (Hourly + Real-Time)"
                                color="success"
                                sx={{ mt: 1 }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Flagged Users Table */}
            {flaggedUsers.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                    <Typography variant="h6">No Suspicious Accounts Found</Typography>
                    <Typography color="text.secondary">
                        All user balances are verified and clean
                    </Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: 'error.light' }}>
                                <TableCell><strong>User</strong></TableCell>
                                <TableCell><strong>Balance</strong></TableCell>
                                <TableCell><strong>Reason</strong></TableCell>
                                <TableCell><strong>Flagged At</strong></TableCell>
                                <TableCell><strong>Flagged By</strong></TableCell>
                                <TableCell align="center"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {flaggedUsers.map((user) => (
                                <TableRow key={user._id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                    {user.fullname}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {user.email}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    @{user.username}
                                                </Typography>
                                            </Box>
                                            <UserActionMenu user={user} />
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={formatCurrency(user.balance)}
                                            color="warning"
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={user.suspiciousBalanceReason || 'No reason provided'}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    maxWidth: 300,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {user.suspiciousBalanceReason || 'No reason provided'}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatDate(user.suspiciousBalanceFlaggedAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={user.suspiciousBalanceFlaggedBy?.fullname || 'Automated System'}
                                            size="small"
                                            color={user.suspiciousBalanceFlaggedBy ? 'primary' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Clear Flag">
                                            <IconButton
                                                color="success"
                                                onClick={() => openUnflagDialog(user)}
                                            >
                                                <RemoveCircleIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Unflag Dialog */}
            <Dialog open={unflagDialog} onClose={() => setUnflagDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Clear Suspicious Balance Flag</DialogTitle>
                <DialogContent>
                    {selectedUser && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                <strong>User:</strong> {selectedUser.fullname} ({selectedUser.email})
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                <strong>Balance:</strong> {formatCurrency(selectedUser.balance)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                <strong>Current Reason:</strong> {selectedUser.suspiciousBalanceReason}
                            </Typography>
                        </Box>
                    )}
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Resolution Notes (Optional)"
                        placeholder="e.g., Verified with user, balance is legitimate..."
                        value={unflagNotes}
                        onChange={(e) => setUnflagNotes(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUnflagDialog(false)}>Cancel</Button>
                    <Button onClick={handleUnflag} variant="contained" color="success">
                        Clear Flag & Allow Transfers
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SuspiciousBalances;
